import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PIPE_SIZES,
  api,
  type BackfillLayerDto,
  type CostSetupDto,
  type PipeDto,
  type PipeRunDto,
  type UserBackfillDefaultDto,
} from '../lib/api';
import {
  Button,
  Card,
  ErrorAlert,
  Field,
  NumberInput,
  Select,
} from '../lib/ui';

type Tab = 'cost-setup' | 'pipes' | 'backfill';

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('cost-setup');
  return (
    <div className="container-fluid">
      <div className="row mt-3">
        <div className="col-12">
          <div className="page-title-box">
            <h4 className="page-title mb-1">My defaults</h4>
            <p className="text-muted mb-0">
              Reusable cost-engine values. New projects start blank — open a project's
              cost tab and click "Apply my defaults" to copy these values onto it.
            </p>
          </div>
          <ul className="nav nav-tabs mb-3">
            {(['cost-setup', 'pipes', 'backfill'] as Tab[]).map((t) => (
              <li key={t} className="nav-item">
                <button
                  type="button"
                  onClick={() => setTab(t)}
                  className={`nav-link ${tab === t ? 'active' : ''}`}
                >
                  {t === 'cost-setup' ? 'Cost setup' : t === 'pipes' ? 'Pipes' : 'Backfill'}
                </button>
              </li>
            ))}
          </ul>

          {tab === 'cost-setup' && <CostSetupTab />}
          {tab === 'pipes' && <PipesTab />}
          {tab === 'backfill' && <BackfillTab />}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Cost setup
// ============================================================================
function CostSetupTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['user-cost-setup'],
    queryFn: () => api.get<CostSetupDto>('/api/v1/user-defaults/cost-setup'),
  });
  const [form, setForm] = useState<CostSetupDto | null>(null);
  useEffect(() => { if (q.data) setForm(q.data); }, [q.data]);

  const save = useMutation({
    mutationFn: (body: CostSetupDto) => api.put<CostSetupDto>('/api/v1/user-defaults/cost-setup', body),
    onSuccess: (data) => {
      qc.setQueryData(['user-cost-setup'], data);
    },
  });

  if (!form) return <p className="text-muted">Loading…</p>;

  const set = <K extends keyof CostSetupDto>(k: K, v: CostSetupDto[K]) => setForm({ ...form, [k]: v });

  return (
    <Card title="Defaults — cost setup" actions={
      <div className="d-flex align-items-center gap-2">
        {save.isSuccess && <span className="text-success small">Saved</span>}
        <Button disabled={save.isPending} onClick={() => save.mutate(form)}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    }>
      <ErrorAlert message={save.isError ? 'Save failed' : null} />
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="ADS 20' stick length (ft)">
          <NumberInput value={form.adsStickLength20} onChange={(e) => set('adsStickLength20', Number(e.target.value))} />
        </Field>
        <Field label="ADS 13' stick length (ft)">
          <NumberInput value={form.adsStickLength13} onChange={(e) => set('adsStickLength13', Number(e.target.value))} />
        </Field>
        <Field label="Other stick length (ft)">
          <NumberInput value={form.otherStickLength} onChange={(e) => set('otherStickLength', Number(e.target.value))} />
        </Field>
        <Field label="ADS crew $/day">
          <NumberInput value={form.adsCrewCost} onChange={(e) => set('adsCrewCost', Number(e.target.value))} />
        </Field>
        <Field label="Other crew $/day">
          <NumberInput value={form.otherCrewCost} onChange={(e) => set('otherCrewCost', Number(e.target.value))} />
        </Field>
        <Field label="ADS minutes / truck">
          <NumberInput value={form.adsTimePerTruckMinutes} onChange={(e) => set('adsTimePerTruckMinutes', Number(e.target.value))} />
        </Field>
        <Field label="ADS labor $/hr">
          <NumberInput value={form.adsLaborRate} onChange={(e) => set('adsLaborRate', Number(e.target.value))} />
        </Field>
        <Field label="Other minutes / truck">
          <NumberInput value={form.otherTimePerTruckMinutes} onChange={(e) => set('otherTimePerTruckMinutes', Number(e.target.value))} />
        </Field>
        <Field label="Other labor $/hr">
          <NumberInput value={form.otherLaborRate} onChange={(e) => set('otherLaborRate', Number(e.target.value))} />
        </Field>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={form.useNestedTruckloads}
          onChange={(e) => set('useNestedTruckloads', e.target.checked)}
        />
        Use nested ADS truckloads
      </label>
    </Card>
  );
}

// ============================================================================
// Pipes
// ============================================================================
function emptyRun(stickLengthFt: number): PipeRunDto {
  return {
    stickLengthFt,
    totalLengthFt: 0,
    adsCostPerFoot: 0,
    adsTruckloadFt: 200,
    adsSticksPerTruck: 0,
    adsMinParentSize: 0,
  };
}

function emptyPipe(sizeInches: number): PipeDto {
  return {
    sizeInches,
    adsInstallRate: 0,
    otherInstallRate: 0,
    otherCostPerFoot: 0,
    otherTruckloadFt: 200,
    runs: [emptyRun(20)],
  };
}

function PipesTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['user-pipes'],
    queryFn: () => api.get<PipeDto[]>('/api/v1/user-defaults/pipes'),
  });
  const [pipes, setPipes] = useState<PipeDto[] | null>(null);
  useEffect(() => { if (q.data) setPipes(q.data); }, [q.data]);

  const save = useMutation({
    mutationFn: (body: PipeDto[]) => api.put<PipeDto[]>('/api/v1/user-defaults/pipes', body),
    onSuccess: (data) => {
      qc.setQueryData(['user-pipes'], data);
      setPipes(data);
    },
  });

  if (!pipes) return <p className="text-sm text-slate-500">Loading…</p>;

  const usedSizes = new Set(pipes.map((p) => p.sizeInches));
  const availableSizes = PIPE_SIZES.filter((s) => !usedSizes.has(s));

  const updatePipe = (idx: number, patch: Partial<PipeDto>) => {
    setPipes(pipes.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };
  const updateRun = (pi: number, ri: number, patch: Partial<PipeRunDto>) => {
    setPipes(pipes.map((p, i) => i !== pi ? p : {
      ...p, runs: p.runs.map((r, j) => j === ri ? { ...r, ...patch } : r),
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Select
            value=""
            onChange={(e) => {
              const sz = Number(e.target.value);
              if (!sz) return;
              setPipes([...pipes, emptyPipe(sz)].sort((a, b) => a.sizeInches - b.sizeInches));
            }}
          >
            <option value="">+ Add size…</option>
            {availableSizes.map((s) => <option key={s} value={s}>{s}"</option>)}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          {save.isSuccess && <span className="text-xs text-emerald-700">Saved</span>}
          <Button disabled={save.isPending} onClick={() => save.mutate(pipes)}>
            {save.isPending ? 'Saving…' : 'Save all'}
          </Button>
        </div>
      </div>

      <ErrorAlert message={save.isError ? 'Save failed' : null} />

      {pipes.length === 0 && (
        <Card title="No pipe defaults yet">
          <p className="text-sm text-slate-500">Add a size above to get started.</p>
        </Card>
      )}

      {pipes.map((p, pi) => (
        <Card
          key={p.sizeInches}
          title={`${p.sizeInches}" pipe defaults`}
          actions={
            <Button
              variant="danger"
              onClick={() => {
                if (!confirm(`Remove ${p.sizeInches}" defaults?`)) return;
                setPipes(pipes.filter((_, i) => i !== pi));
              }}
            >
              Remove
            </Button>
          }
        >
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="ADS install (ft/day)">
              <NumberInput value={p.adsInstallRate} onChange={(e) => updatePipe(pi, { adsInstallRate: Number(e.target.value) })} />
            </Field>
            <Field label="Other install (ft/day)">
              <NumberInput value={p.otherInstallRate} onChange={(e) => updatePipe(pi, { otherInstallRate: Number(e.target.value) })} />
            </Field>
            <Field label="Other $/ft">
              <NumberInput value={p.otherCostPerFoot} onChange={(e) => updatePipe(pi, { otherCostPerFoot: Number(e.target.value) })} />
            </Field>
            <Field label="Other ft / truckload">
              <NumberInput value={p.otherTruckloadFt} onChange={(e) => updatePipe(pi, { otherTruckloadFt: Number(e.target.value) })} />
            </Field>
          </div>

          <div className="mt-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-700">ADS stick lengths</h3>
              <Button
                variant="secondary"
                onClick={() => {
                  const used = new Set(p.runs.map((r) => r.stickLengthFt));
                  const next = !used.has(20) ? 20 : !used.has(13) ? 13 : 0;
                  if (!next) return;
                  updatePipe(pi, { runs: [...p.runs, emptyRun(next)] });
                }}
                disabled={p.runs.length >= 2}
              >
                + Add stick length
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-1 text-left">Stick (ft)</th>
                    <th className="px-2 py-1 text-left">ADS $/ft</th>
                    <th className="px-2 py-1 text-left">ADS ft / truckload</th>
                    <th className="px-2 py-1 text-left">Sticks / truck</th>
                    <th className="px-2 py-1 text-left">Min parent size</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {p.runs.map((r, ri) => (
                    <tr key={ri} className="border-t border-slate-100">
                      <td className="px-2 py-1">
                        <NumberInput value={r.stickLengthFt} onChange={(e) => updateRun(pi, ri, { stickLengthFt: Number(e.target.value) })} className="!w-24" />
                      </td>
                      <td className="px-2 py-1">
                        <NumberInput value={r.adsCostPerFoot} onChange={(e) => updateRun(pi, ri, { adsCostPerFoot: Number(e.target.value) })} className="!w-28" />
                      </td>
                      <td className="px-2 py-1">
                        <NumberInput value={r.adsTruckloadFt} onChange={(e) => updateRun(pi, ri, { adsTruckloadFt: Number(e.target.value) })} className="!w-32" />
                      </td>
                      <td className="px-2 py-1">
                        <NumberInput value={r.adsSticksPerTruck} onChange={(e) => updateRun(pi, ri, { adsSticksPerTruck: Number(e.target.value) })} className="!w-28" />
                      </td>
                      <td className="px-2 py-1">
                        <NumberInput value={r.adsMinParentSize} onChange={(e) => updateRun(pi, ri, { adsMinParentSize: Number(e.target.value) })} className="!w-28" />
                      </td>
                      <td className="px-2 py-1">
                        <Button
                          variant="secondary"
                          onClick={() => updatePipe(pi, { runs: p.runs.filter((_, j) => j !== ri) })}
                          disabled={p.runs.length <= 1}
                        >
                          ✕
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ============================================================================
// Backfill
// ============================================================================
const PRODUCTS = ['N-12', 'HP Storm', 'RCP', 'CMP', 'other'];
const UNITS = ['Imperial', 'Metric'];
const TRENCH_BOX_MODES: { value: string; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'ads', label: 'ADS only' },
  { value: 'both', label: 'Both sides' },
];

function emptyLayer(): BackfillLayerDto {
  return { cost: 0, density: 1.5, wastePct: 0, isNative: false };
}

function emptySide(side: 'ads' | 'other'): UserBackfillDefaultDto {
  return {
    side,
    product: side === 'ads' ? 'N-12' : 'RCP',
    unit: 'Imperial',
    trenchBoxMode: 'none',
    spoilDisposalRate: 0,
    bedding: emptyLayer(),
    springline: emptyLayer(),
    topOfPipe: emptyLayer(),
    sixOver: emptyLayer(),
    twelveOver: emptyLayer(),
  };
}

function BackfillTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['user-backfill'],
    queryFn: () => api.get<UserBackfillDefaultDto[]>('/api/v1/user-defaults/backfill'),
  });
  const [sides, setSides] = useState<UserBackfillDefaultDto[] | null>(null);
  useEffect(() => {
    if (!q.data) return;
    // Always present both ads + other slots so the user can fill either.
    const ads = q.data.find((s) => s.side === 'ads') ?? emptySide('ads');
    const other = q.data.find((s) => s.side === 'other') ?? emptySide('other');
    setSides([ads, other]);
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: UserBackfillDefaultDto[]) =>
      api.put<UserBackfillDefaultDto[]>('/api/v1/user-defaults/backfill', body),
    onSuccess: (data) => qc.setQueryData(['user-backfill'], data),
  });

  if (!sides) return <p className="text-sm text-slate-500">Loading…</p>;

  const updateSide = (i: number, patch: Partial<UserBackfillDefaultDto>) => {
    setSides(sides.map((s, j) => j === i ? { ...s, ...patch } : s));
  };
  const updateLayer = (i: number, key: keyof UserBackfillDefaultDto, patch: Partial<BackfillLayerDto>) => {
    setSides(sides.map((s, j) => j !== i ? s : {
      ...s, [key]: { ...(s[key] as BackfillLayerDto), ...patch },
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        {save.isSuccess && <span className="text-xs text-emerald-700">Saved</span>}
        <Button disabled={save.isPending} onClick={() => save.mutate(sides)}>
          {save.isPending ? 'Saving…' : 'Save all'}
        </Button>
      </div>
      <ErrorAlert message={save.isError ? 'Save failed' : null} />

      {sides.map((s, i) => (
        <Card key={s.side} title={s.side === 'ads' ? 'ADS side' : 'Other side'}>
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Product">
              <Select value={s.product} onChange={(e) => updateSide(i, { product: e.target.value })}>
                {PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
            <Field label="Unit">
              <Select value={s.unit} onChange={(e) => updateSide(i, { unit: e.target.value })}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </Select>
            </Field>
            <Field label="Trench box">
              <Select value={s.trenchBoxMode} onChange={(e) => updateSide(i, { trenchBoxMode: e.target.value })}>
                {TRENCH_BOX_MODES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </Field>
            <Field label="Spoil disposal $/CY">
              <NumberInput value={s.spoilDisposalRate} onChange={(e) => updateSide(i, { spoilDisposalRate: Number(e.target.value) })} />
            </Field>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1 text-left">Layer</th>
                  <th className="px-2 py-1 text-left">Cost</th>
                  <th className="px-2 py-1 text-left">Density</th>
                  <th className="px-2 py-1 text-left">Waste %</th>
                  <th className="px-2 py-1 text-left">Native</th>
                </tr>
              </thead>
              <tbody>
                {(['bedding', 'springline', 'topOfPipe', 'sixOver', 'twelveOver'] as const).map((key) => {
                  const layer = s[key] as BackfillLayerDto;
                  return (
                    <tr key={key} className="border-t border-slate-100">
                      <td className="px-2 py-1 text-slate-700">{labelFor(key)}</td>
                      <td className="px-2 py-1">
                        <NumberInput value={layer.cost} onChange={(e) => updateLayer(i, key, { cost: Number(e.target.value) })} className="!w-28" />
                      </td>
                      <td className="px-2 py-1">
                        <NumberInput value={layer.density} onChange={(e) => updateLayer(i, key, { density: Number(e.target.value) })} className="!w-24" />
                      </td>
                      <td className="px-2 py-1">
                        <NumberInput value={layer.wastePct} onChange={(e) => updateLayer(i, key, { wastePct: Number(e.target.value) })} className="!w-24" />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="checkbox"
                          checked={layer.isNative}
                          onChange={(e) => updateLayer(i, key, { isNative: e.target.checked })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}

function labelFor(key: 'bedding' | 'springline' | 'topOfPipe' | 'sixOver' | 'twelveOver'): string {
  return key === 'topOfPipe' ? 'Top of pipe'
    : key === 'sixOver' ? '6" over'
      : key === 'twelveOver' ? '12" over'
        : key.charAt(0).toUpperCase() + key.slice(1);
}
